import components.GameApp
import game.gameStore
import react.FC
import react.Props
import react.create
import react.dom.DangerouslySetInnerHTML
import react.dom.client.createRoot
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.style
import web.cssom.ClassName
import web.dom.ElementId
import web.dom.document
import web.html.HtmlSource
import web.window.window as windowGlobal

val App = FC("App") {
    div {
        className = ClassName("app-root")

        style {
            dangerouslySetInnerHTML = DangerouslySetInnerHTML(HtmlSource(generateStyles()))
        }

        GameApp()
    }
}

fun generateStyles(): String {
    return """
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        html, body, #root {
            width: 100%;
            height: 100%;
            overflow: hidden;
        }

        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0f172a;
            color: #e2e8f0;
        }

        .app-root {
            width: 100%;
            height: 100%;
        }

        .game-root {
            position: relative;
            width: 100%;
            height: 100%;
        }

        .game-canvas {
            position: absolute;
            inset: 0;
        }

        .game-canvas canvas {
            display: block;
        }

        /* ===== HUD ===== */
        .hud {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            padding: 16px 20px;
            pointer-events: none;
            z-index: 10;
        }

        .hud-top {
            display: flex;
            justify-content: space-between;
            gap: 16px;
            background: rgba(15, 23, 42, 0.7);
            border-radius: 12px;
            padding: 10px 18px;
            backdrop-filter: blur(4px);
        }

        .hud-score,
        .hud-wave,
        .hud-kills {
            font-size: 16px;
            font-weight: 600;
            color: #f8fafc;
        }

        .hud-enemies {
            display: inline-block;
            margin-top: 10px;
            padding: 6px 14px;
            background: rgba(220, 38, 38, 0.7);
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
        }

        .hud-hpbar {
            position: relative;
            margin-top: 10px;
            width: 280px;
            height: 24px;
            background: rgba(15, 23, 42, 0.8);
            border-radius: 12px;
            overflow: hidden;
            border: 2px solid rgba(255, 255, 255, 0.2);
        }

        .hud-hpbar-fill {
            height: 100%;
            border-radius: 10px;
            transition: width 0.15s ease, background 0.3s ease;
        }

        .hud-hp-text {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
            font-weight: 700;
            color: white;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
        }

        .hud-energybar {
            position: relative;
            margin-top: 6px;
            width: 280px;
            height: 16px;
            background: rgba(15, 23, 42, 0.8);
            border-radius: 8px;
            overflow: hidden;
            border: 2px solid rgba(234, 179, 8, 0.3);
        }

        .hud-energybar-fill {
            height: 100%;
            border-radius: 6px;
            transition: width 0.15s ease;
        }

        .hud-message {
            position: absolute;
            top: 45%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 28px;
            font-weight: 800;
            color: #fbbf24;
            text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
            background: rgba(15, 23, 42, 0.6);
            padding: 12px 28px;
            border-radius: 12px;
            animation: fadeIn 0.3s ease;
        }

        /* ===== 技能栏 ===== */
        .hud-skills {
            position: absolute;
            bottom: 24px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 12px;
            pointer-events: none;
        }

        .skill-btn {
            position: relative;
            width: 88px;
            height: 72px;
            background: rgba(30, 41, 59, 0.85);
            border: 2px solid #475569;
            border-radius: 12px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            overflow: hidden;
        }

        .skill-btn.skill-ready {
            border-color: #3b82f6;
            box-shadow: 0 0 12px rgba(59, 130, 246, 0.4);
        }

        .skill-key {
            font-size: 20px;
            font-weight: 800;
            color: #f8fafc;
        }

        .skill-label {
            font-size: 12px;
            color: #94a3b8;
        }

        .skill-cooldown {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            font-weight: 800;
            color: white;
            padding: 4px 0;
        }

        /* ===== buff 提示 ===== */
        .hud-buffs {
            position: absolute;
            bottom: 110px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 8px;
            pointer-events: none;
        }

        .buff-tag {
            padding: 6px 14px;
            border-radius: 16px;
            font-size: 13px;
            font-weight: 700;
            color: white;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
            animation: buffPulse 1s ease-in-out infinite alternate;
        }

        .buff-shield { background: #facc15; }
        .buff-speed  { background: #38bdf8; }
        .buff-rapid  { background: #f97316; }
        .buff-multi  { background: #a855f7; }

        @keyframes buffPulse {
            from { opacity: 0.7; transform: scale(0.98); }
            to   { opacity: 1; transform: scale(1.04); }
        }

        /* ===== 覆盖层(菜单 / 结束) ===== */
        .game-overlay {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(2, 6, 23, 0.75);
            backdrop-filter: blur(6px);
            z-index: 20;
        }

        .overlay-card {
            text-align: center;
            background: #1e293b;
            padding: 40px 56px;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }

        .overlay-card h1 {
            font-size: 40px;
            margin-bottom: 16px;
            color: #f8fafc;
        }

        .overlay-card p {
            font-size: 16px;
            color: #94a3b8;
            margin-bottom: 8px;
        }

        .overlay-card p:last-of-type {
            margin-bottom: 24px;
        }

        .btn-start {
            padding: 14px 44px;
            background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 18px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.3s ease;
        }

        .btn-start:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(59, 130, 246, 0.5);
        }

        .btn-menu {
            margin-left: 12px;
            padding: 14px 30px;
            background: transparent;
            color: #94a3b8;
            border: 2px solid #475569;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
        }

        .btn-menu:hover {
            border-color: #94a3b8;
            color: white;
        }

        .btn-small {
            padding: 8px 20px;
            font-size: 14px;
            border-radius: 8px;
            margin-top: 8px;
        }

        .overlay-wide {
            max-width: 720px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
        }

        .overlay-coins {
            color: #facc15;
            font-weight: 700;
            font-size: 18px;
        }

        /* ===== 车库 / 商店卡片 ===== */
        .shop-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: center;
            margin: 20px 0;
        }

        .shop-card {
            background: #0f172a;
            border: 2px solid #334155;
            border-radius: 14px;
            padding: 16px;
            width: 200px;
            text-align: center;
            transition: all 0.3s ease;
        }

        .shop-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
        }

        .shop-card.shop-selected {
            border-color: #3b82f6;
            box-shadow: 0 0 15px rgba(59, 130, 246, 0.4);
        }

        .shop-card h2 {
            font-size: 20px;
            margin-bottom: 8px;
            color: #f8fafc;
        }

        .shop-card p {
            font-size: 13px;
            color: #94a3b8;
            margin-bottom: 8px;
        }

        .shop-state {
            display: inline-block;
            padding: 8px 18px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 700;
            background: #22c55e;
            color: white;
        }

        .shop-state.shop-locked {
            background: #475569;
            color: #94a3b8;
        }

        .hud-coins {
            color: #facc15;
            font-weight: 800;
        }

        .hud-weapon {
            font-size: 14px;
            color: #a78bfa;
        }

        .hud-hint {
            position: absolute;
            bottom: 110px;
            left: 50%;
            transform: translateX(-50%);
            font-size: 13px;
            color: #94a3b8;
            background: rgba(15, 23, 42, 0.6);
            padding: 6px 16px;
            border-radius: 8px;
            pointer-events: none;
        }

        .btn-pause {
            position: absolute;
            top: 70px;
            right: 20px;
            width: 44px;
            height: 44px;
            border: none;
            border-radius: 10px;
            background: rgba(30, 41, 59, 0.85);
            color: #e2e8f0;
            font-size: 20px;
            cursor: pointer;
            z-index: 15;
            transition: all 0.3s ease;
        }

        .btn-pause:hover {
            background: #3b82f6;
        }

        /* ===== 调试面板 ===== */
        .debug-toggle {
            position: absolute;
            bottom: 20px;
            right: 20px;
            padding: 10px 18px;
            background: rgba(30, 41, 59, 0.85);
            border: 2px solid #64748b;
            border-radius: 10px;
            color: #e2e8f0;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            z-index: 30;
            transition: all 0.3s ease;
        }

        .debug-toggle:hover {
            border-color: #f59e0b;
            color: #fbbf24;
        }

        .debug-panel {
            position: absolute;
            top: 10px;
            left: 10px;
            width: 360px;
            max-height: calc(100vh - 80px);
            overflow-y: auto;
            background: rgba(15, 23, 42, 0.92);
            border: 2px solid #334155;
            border-radius: 14px;
            padding: 16px;
            z-index: 30;
            backdrop-filter: blur(6px);
        }

        .debug-panel h2 {
            font-size: 18px;
            margin-bottom: 10px;
            color: #fbbf24;
        }

        .debug-label {
            font-size: 13px;
            font-weight: 700;
            color: #94a3b8;
            margin: 10px 0 6px;
        }

        .debug-row {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
        }

        .debug-btn {
            padding: 5px 10px;
            border: none;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            color: white;
        }

        .debug-btn:hover {
            transform: translateY(-1px);
            filter: brightness(1.2);
        }

        .debug-btn-gold    { background: #f59e0b; }
        .debug-btn-god     { background: #ef4444; }
        .debug-btn-weapon  { background: #3b82f6; }
        .debug-btn-chassis { background: #8b5cf6; }
        .debug-btn-skill   { background: #10b981; }
        .debug-btn-powerup { background: #a855f7; }
        .debug-btn-toggle  { background: #64748b; }

        @keyframes fadeIn {
            from { opacity: 0; transform: translate(-50%, -60%); }
            to { opacity: 1; transform: translate(-50%, -50%); }
        }
    """.trimIndent()
}

fun main() {
    // 调试用:将 Redux Store 暴露到 window,可在浏览器控制台访问
    windowGlobal.asDynamic().reduxStore = gameStore

    val container = document.getElementById(ElementId("root"))
        ?: error("未找到 id 为 'root' 的容器元素,请检查 index.html")
    createRoot(container).render(App.create())
}
